import React, { useState } from 'react';
import { Shield, Lock, Database, User, Play, ChevronRight, Compass, Map, Rocket, ArrowRight, Loader2, CheckCircle } from 'lucide-react';
import * as Icons from 'lucide-react';
import siteConfig from '../data/siteConfig.json';
import benefits from '../data/benefits.json';
import howItWorks from '../data/howItWorks.json';
import useCasesData from '../data/useCases.json';
import GlassCard from '../components/GlassCard';
import { useVideoModal } from '../components/VideoModal';
import VideoModal from '../components/VideoModal';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TrustIcon = ({ icon }) => {
  const iconMap = { Shield, Lock, Database, User };
  const IconComponent = iconMap[icon];
  return IconComponent ? <IconComponent size={20} /> : null;
};

const Home = () => {
  const { isOpen, openModal, closeModal } = useVideoModal();
  const [email, setEmail] = useState('');
  const [checkboxes, setCheckboxes] = useState({
    investor: false,
    product: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/early-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          product_updates: checkboxes.product,
          investor_updates: checkboxes.investor,
        }),
      });
      
      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative">
      {/* HERO SECTION - Full Video Background */}
      <section className="relative min-h-screen overflow-hidden">
        {/* Video Background - Full Edge-to-Edge, Slowed Down */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="hero-video-bg"
          data-testid="hero-video-bg"
          ref={(el) => { if (el) el.playbackRate = 0.4; }}
        >
          <source 
            src="https://customer-assets.emergentagent.com/job_presence-system-9/artifacts/v8h9ynrt_20250911_0033_Cosmic%20Scent%20Revelation_simple_compose_01k4t44j4gfm9b7vbqzbqz40n4.mp4" 
            type="video/mp4" 
          />
        </video>
        
        {/* Dark Overlay for Readability */}
        <div className="hero-overlay" />
        
        {/* Content Layer */}
        <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 md:px-12 pt-24 pb-16">
          {/* New Hero Logo - Clean, Transparent, Larger */}
          <div className="flex justify-center mb-8 animate-fade-in">
            <img
              src="https://customer-assets.emergentagent.com/job_presence-system-9/artifacts/87b4ulqj_file_000000001c7c7209864f481224b34516%20%282%29.png"
              alt="ROMIND"
              className="hero-logo-main"
              data-testid="hero-logo"
            />
          </div>
          
          {/* Wordmark with Premium Gradient */}
          <h1 
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight mb-4 animate-slide-up text-center"
            data-testid="hero-headline"
          >
            <span className="hero-wordmark">{siteConfig.brand.name}</span>
          </h1>
          
          {/* Tagline */}
          <p 
            className="text-xl md:text-2xl lg:text-3xl text-white/90 font-light mb-6 animate-slide-up text-center"
            style={{ animationDelay: '0.1s' }}
          >
            {siteConfig.hero.headline.replace('ROMIND — ', '')}
          </p>
          
          {/* Subheadline */}
          <p 
            className="max-w-2xl mx-auto text-base md:text-lg text-white/70 leading-relaxed mb-10 animate-slide-up text-center"
            style={{ animationDelay: '0.2s' }}
            data-testid="hero-subheadline"
          >
            {siteConfig.hero.subheadline}
          </p>
          
          {/* CTAs */}
          <div 
            className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up"
            style={{ animationDelay: '0.3s' }}
          >
            <a
              href="#early-access"
              className="btn-primary text-lg px-10 py-4"
              data-testid="hero-cta-primary"
            >
              {siteConfig.cta.primary.text}
            </a>
            <button
              onClick={openModal}
              className="btn-ghost text-lg px-10 py-4 flex items-center justify-center gap-2"
              data-testid="hero-cta-secondary"
            >
              <Play size={20} />
              {siteConfig.cta.secondary.text}
            </button>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-float z-20">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-violet-400 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="relative py-12 border-y border-white/5" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {siteConfig.trustStrip.map((item, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 text-slate-400"
                data-testid={`trust-item-${index}`}
              >
                <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center text-violet-400">
                  <TrustIcon icon={item.icon} />
                </div>
                <span className="text-sm font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT ROMIND IS */}
      <section className="section-padding" data-testid="what-romind-is">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-sm font-mono tracking-widest uppercase text-violet-400 mb-4 block">
                Beyond Chat
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                ROMIND is not just another assistant.
              </h2>
              <p className="text-lg text-slate-400 leading-relaxed mb-8">
                It's a structured cognitive core designed to understand context, maintain continuity, 
                and provide meaningful assistance across every aspect of your life.
              </p>
              <Link 
                to="/features" 
                className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300 transition-colors group"
                data-testid="learn-more-link"
              >
                Explore the architecture
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {['Memory', 'Initiative', 'Safety Logic', 'Context Awareness'].map((item, index) => (
                <div 
                  key={item}
                  className="glass-panel p-6 text-center"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <span className="text-lg font-semibold text-white">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* KEY BENEFITS */}
      <section className="section-padding cosmic-bg" data-testid="key-benefits">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm font-mono tracking-widest uppercase text-violet-400 mb-4 block">
              Capabilities
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
              What ROMIND brings to you
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <GlassCard
                key={benefit.id}
                icon={benefit.icon}
                title={benefit.title}
                description={benefit.description}
                highlight={benefit.highlight}
                data-testid={`benefit-card-${benefit.id}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT PREVIEW */}
      <section className="section-padding" data-testid="product-preview">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm font-mono tracking-widest uppercase text-violet-400 mb-4 block">
              Preview
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
              ROMIND in action
            </h2>
          </div>
          
          <div className="relative">
            {/* Glow effect */}
            <div 
              className="absolute inset-0 rounded-3xl opacity-50"
              style={{
                background: 'radial-gradient(ellipse at center, rgba(124, 58, 237, 0.3) 0%, transparent 70%)',
                filter: 'blur(60px)',
              }}
            />
            
            {/* Device mockups */}
            <div className="relative glass-panel p-8 md:p-12 rounded-3xl">
              <div className="grid md:grid-cols-3 gap-8 items-center">
                {/* Phone mockup */}
                <div className="flex justify-center">
                  <div className="w-48 h-96 rounded-3xl bg-gradient-to-b from-[#1a1035] to-[#0B0620] border border-white/10 p-3 relative overflow-hidden">
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 w-20 h-6 bg-black rounded-full" />
                    <div className="w-full h-full rounded-2xl bg-[#05030A] flex items-center justify-center">
                      <img src={siteConfig.brand.logo} alt="ROMIND" className="w-16 h-16 opacity-50" />
                    </div>
                  </div>
                </div>
                
                {/* Center content */}
                <div className="text-center md:col-span-1">
                  <h3 className="text-2xl font-semibold text-white mb-4">Everywhere you need it</h3>
                  <p className="text-slate-400">
                    ROMIND is designed to be your cognitive companion across all devices and platforms.
                  </p>
                </div>
                
                {/* Desktop mockup */}
                <div className="flex justify-center">
                  <div className="w-64 rounded-xl bg-gradient-to-b from-[#1a1035] to-[#0B0620] border border-white/10 p-2">
                    <div className="h-4 flex items-center gap-1.5 px-2 mb-2">
                      <span className="w-2 h-2 rounded-full bg-red-500/50" />
                      <span className="w-2 h-2 rounded-full bg-yellow-500/50" />
                      <span className="w-2 h-2 rounded-full bg-green-500/50" />
                    </div>
                    <div className="aspect-video rounded-lg bg-[#05030A] flex items-center justify-center">
                      <img src={siteConfig.brand.logo} alt="ROMIND" className="w-12 h-12 opacity-50" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section-padding cosmic-bg" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm font-mono tracking-widest uppercase text-violet-400 mb-4 block">
              Process
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {howItWorks.title}
            </h2>
            <p className="text-slate-400 text-lg">{howItWorks.subtitle}</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {howItWorks.steps.map((step, index) => {
              const IconComponent = Icons[step.icon];
              return (
                <div 
                  key={step.number}
                  className="relative group"
                  data-testid={`how-it-works-step-${index + 1}`}
                >
                  {/* Connection line */}
                  {index < howItWorks.steps.length - 1 && (
                    <div className="hidden md:block absolute top-16 left-full w-full h-px bg-gradient-to-r from-violet-500/50 to-transparent z-0" />
                  )}
                  
                  <div className="glass-panel p-8 relative z-10 h-full">
                    <div className="flex items-center gap-4 mb-6">
                      <span className="text-4xl font-bold text-violet-500/30">{step.number}</span>
                      {IconComponent && (
                        <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center text-violet-400 group-hover:bg-violet-500/20 transition-colors">
                          <IconComponent size={24} />
                        </div>
                      )}
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3">{step.title}</h3>
                    <p className="text-slate-400">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* USE CASES PREVIEW */}
      <section className="section-padding" data-testid="use-cases-preview">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <span className="text-sm font-mono tracking-widest uppercase text-violet-400 mb-4 block">
                Applications
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
                Adapt to how you work
              </h2>
            </div>
            <Link 
              to="/use-cases" 
              className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300 transition-colors group shrink-0"
              data-testid="view-all-use-cases"
            >
              View all use cases
              <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {useCasesData.cases.slice(0, 4).map((useCase) => {
              const IconComponent = Icons[useCase.icon];
              return (
                <div
                  key={useCase.id}
                  className={`glass-panel p-8 group hover:border-${useCase.color === 'magenta' ? 'pink' : 'violet'}-500/30 transition-all`}
                  data-testid={`use-case-card-${useCase.id}`}
                >
                  <div className={`w-12 h-12 rounded-xl ${
                    useCase.color === 'magenta' ? 'bg-pink-500/10 text-pink-400' : 'bg-violet-500/10 text-violet-400'
                  } flex items-center justify-center mb-6`}>
                    {IconComponent && <IconComponent size={24} />}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{useCase.title}</h3>
                  <p className="text-sm text-slate-500 mb-4">{useCase.subtitle}</p>
                  <p className="text-slate-400">{useCase.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF - Founder Note */}
      <section className="section-padding cosmic-bg" data-testid="founder-note">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-sm font-mono tracking-widest uppercase text-violet-400 mb-8 block">
            From the Founder
          </span>
          <blockquote className="text-2xl md:text-3xl font-light text-white leading-relaxed mb-8">
            "ROMIND was born from a simple observation: the AI systems we use forget us the moment a 
            conversation ends. I wanted to build something different—a cognitive presence that grows 
            with you while keeping you in complete control."
          </blockquote>
          <cite className="text-slate-400 not-italic">— Founder of ROMIND</cite>
        </div>
      </section>

      {/* LEAD CAPTURE */}
      <section id="early-access" className="section-padding" data-testid="lead-capture">
        <div className="max-w-2xl mx-auto">
          <div className="glass-panel p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Join the ROMIND early list
            </h2>
            <p className="text-slate-400 mb-8">
              Be among the first to experience cognitive AI presence. Limited invitations opening soon.
            </p>
            
            <form onSubmit={handleEmailSubmit} className="space-y-6">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="input-dark w-full text-center"
                required
                data-testid="email-input"
              />
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center text-sm text-slate-400">
                <label className="flex items-center gap-2 cursor-pointer hover:text-white transition-colors">
                  <input
                    type="checkbox"
                    checked={checkboxes.product}
                    onChange={(e) => setCheckboxes({ ...checkboxes, product: e.target.checked })}
                    className="rounded border-white/20 bg-white/5 text-violet-500 focus:ring-violet-500/20"
                    data-testid="checkbox-product"
                  />
                  Product updates
                </label>
                <label className="flex items-center gap-2 cursor-pointer hover:text-white transition-colors">
                  <input
                    type="checkbox"
                    checked={checkboxes.investor}
                    onChange={(e) => setCheckboxes({ ...checkboxes, investor: e.target.checked })}
                    className="rounded border-white/20 bg-white/5 text-violet-500 focus:ring-violet-500/20"
                    data-testid="checkbox-investor"
                  />
                  Investor updates
                </label>
              </div>
              
              <button
                type="submit"
                className="btn-primary w-full sm:w-auto flex items-center justify-center gap-2"
                data-testid="submit-email-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Get Early Access'
                )}
              </button>
              
              {submitStatus === 'success' && (
                <div className="flex items-center justify-center gap-2 p-3 rounded-xl bg-green-500/10 border border-green-500/20 text-green-400 text-sm">
                  <CheckCircle size={18} />
                  <span>You're on the list!</span>
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm text-center">
                  Something went wrong. Please try again.
                </div>
              )}
            </form>
            
            <p className="text-xs text-slate-500 mt-6">
              We respect your privacy. No spam, ever.
            </p>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      <VideoModal isOpen={isOpen} onClose={closeModal} />
    </div>
  );
};

export default Home;
